import queryString from 'querystring';
import { logError } from 'commons/logger';
import getEnvValue from 'commons/getEnvValue';

// invalid with suggestions
const INVALID_ADDRESS_W_SUGGESTIONS_FOOTNOTE_CODES = [
  'A#',
  'B#',
  'G#',
  'K#',
  'L#',
  'M#',
  'N#',
  'O#',
  'P#',
  'S#',
  'U#',
  'Z#',
];
const INVALID_ADDRESS_W_SUGGESTIONS_DPV_MATCH_CODES = ['D', 'S'];
const INVALID_ADDRESS_VALUE_DPV_FOOTNOTE_CODES = ['AA', 'A1', 'TA', 'M1', 'M3'];
const INVALID_CITY_VALUE_DPV_FOOTNOTE_CODE = 'BB';
const INVALID_SECONDARY_ADDRESS_VALUE_DPV_FOOTNOTE_CODES = ['CC', 'C1'];

// invalid "WITHOUT" suggestions
const INVALID_ADDRESS_WITHOUT_SUGGESTIONS_FOOTNOTE_CODES = [
  'C#',
  'D#',
  'F#',
  'H#',
  'I#',
  'J#',
  'V#',
  'W#',
];
const DPV_MATCH_CODE_NONE = 'N';

export const validateAddress = async shippingState => {
  const {
    address: street,
    city,
    apartment: secondary,
    state,
    zipcode,
  } = shippingState;

  const EMBED_KEY =
    getEnvValue('NEXT_PUBLIC_SMARTY_STREET_EMBED_KEY') ??
    getEnvValue('REACT_APP_SMARTY_STREET_EMBED_KEY') ??
    '';

  const requestUrl = `https://us-street.api.smarty.com/street-address?${queryString.stringify(
    {
      key: EMBED_KEY,
      street,
      city,
      secondary,
      state,
      zipcode,
      match: 'enhanced',
    }
  )}`;

  const response = await fetch(requestUrl, {
    headers: {
      Referer: 'https://joybird.com',
    },
    method: 'GET',
  })
    .then(res => {
      if (res.status === 200) {
        return res.json();
      }
      // handle other http response codes if needed
      return [];
    })
    .catch(e => {
      logError(e);
    });

  // cases: invalid, invalid with suggestions, valid, ignored

  if (response?.length && response?.length > 0) {
    // only considering the first suggestion
    const {
      delivery_line_1,
      components: suggestionComponents,
      analysis: { dpv_match_code, dpv_footnotes, footnotes, dpv_vacant },
    } = response[0];

    const footnotes_array =
      footnotes &&
      footnotes?.split('#').map(footnoteCode => `${footnoteCode}#`);

    if (dpv_vacant === 'Y') {
      // address is vacant
      return {
        showSuggestion: true,
        suggestion: null,
      };
    }
    if (
      INVALID_ADDRESS_W_SUGGESTIONS_DPV_MATCH_CODES.includes(dpv_match_code) || // invalid with suggestions
      INVALID_ADDRESS_W_SUGGESTIONS_FOOTNOTE_CODES.some(ref_code =>
        footnotes_array?.some(footnotes_code => footnotes_code === ref_code)
      )
    ) {
      return {
        showSuggestion: true,
        suggestion: {
          ...(INVALID_ADDRESS_VALUE_DPV_FOOTNOTE_CODES.some(code =>
            dpv_footnotes.includes(code)
          ) &&
          delivery_line_1
            .split(suggestionComponents.secondary_designator)?.[0]
            ?.trim()
            ? {
                address:
                  INVALID_ADDRESS_VALUE_DPV_FOOTNOTE_CODES.some(code =>
                    dpv_footnotes.includes(code)
                  ) &&
                  delivery_line_1
                    .split(suggestionComponents.secondary_designator)?.[0]
                    ?.trim(),
              }
            : {}),
          ...(dpv_footnotes.includes(INVALID_CITY_VALUE_DPV_FOOTNOTE_CODE) &&
          suggestionComponents?.city_name &&
          suggestionComponents.city_name?.toLowerCase() !==
            city?.toLowerCase() &&
          suggestionComponents.city_name
            ? {
                city:
                  dpv_footnotes.includes(
                    INVALID_CITY_VALUE_DPV_FOOTNOTE_CODE
                  ) &&
                  suggestionComponents?.city_name &&
                  suggestionComponents.city_name?.toLowerCase() !==
                    city?.toLowerCase() &&
                  suggestionComponents.city_name,
              }
            : {}),
          ...(INVALID_SECONDARY_ADDRESS_VALUE_DPV_FOOTNOTE_CODES.some(code =>
            dpv_footnotes.includes(code)
          )
            ? {
                apartment: delivery_line_1
                  .split(suggestionComponents.secondary_designator)?.[1]
                  ?.trim(),
              }
            : {}),
          ...(suggestionComponents?.state_abbreviation !==
            shippingState?.state && suggestionComponents?.state_abbreviation
            ? {
                state: suggestionComponents?.state_abbreviation,
              }
            : {}),
          ...(suggestionComponents?.zipcode !== shippingState?.state &&
          suggestionComponents?.zipcode
            ? {
                zipcode: suggestionComponents?.zipcode,
              }
            : {}),
        },
      };
    }
    if (
      !dpv_match_code ||
      dpv_match_code === DPV_MATCH_CODE_NONE || // invalid without suggestions
      INVALID_ADDRESS_WITHOUT_SUGGESTIONS_FOOTNOTE_CODES.some(ref_code =>
        footnotes_array?.some(footnotes_code => footnotes_code === ref_code)
      )
    ) {
      return {
        showSuggestion: true,
        suggestion: null,
      };
    }
  }

  return {
    showSuggestion: false,
    suggestion: null,
  };
};
